import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { benefitGroups } from "@/components/admin/profile-editor/sections/BenefitsSection";

import { COMPENSATION_MODEL_OPTIONS, TRAINING_FREQUENCY_OPTIONS } from "@/lib/orgProfileOptions";

// Compensation models and training cadence come from the canonical shared
// lists the profile editor sections render, so the wizard writes the exact
// keys/labels the public profile and fit scoring already read; benefits come
// from BenefitsSection's grouped list.
//
// There is deliberately no rating prompt on this step: organizations describe
// what they offer but never rate themselves (#606). Ratings come only from
// signed-in agents' reviews.

const compensationModels = COMPENSATION_MODEL_OPTIONS;

const trainingFreqOptions = TRAINING_FREQUENCY_OPTIONS;

interface Step4Props {
  data: {
    compensationModels: string[];
    providesLeads: boolean;
    trainingFrequency: string[];
    selectedBenefits: string[];
  };
  onUpdate: (data: any) => void;
}

const OrgOnboardingStep4 = ({ data, onUpdate }: Step4Props) => {
  const toggleArray = (key: "compensationModels" | "trainingFrequency" | "selectedBenefits", value: string) => {
    const current = data[key] || [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    onUpdate({ ...data, [key]: next });
  };

  // Fifty benefits in six groups do not fit one screen, so one group shows at
  // a time; each tab carries its own selected count so nothing chosen in
  // another group goes out of sight.
  const [activeGroup, setActiveGroup] = useState(benefitGroups[0].title);
  const group = benefitGroups.find((g) => g.title === activeGroup) ?? benefitGroups[0];
  const selectedIn = (g: (typeof benefitGroups)[number]) =>
    g.items.filter((item) => (data.selectedBenefits || []).includes(item.label)).length;

  const chip = (selected: boolean) =>
    cn(
      "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
      selected
        ? "bg-primary text-primary-foreground border-primary"
        : "bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
    );

  return (
    <div className="space-y-5">
      {/* Compensation models */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Label>What is your compensation model?</Label>
          <p className="text-xs text-muted-foreground">Select all that apply — no specific numbers needed.</p>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {compensationModels.map((model) => (
            <button
              key={model.key}
              type="button"
              onClick={() => toggleArray("compensationModels", model.key)}
              className={chip((data.compensationModels || []).includes(model.key))}
            >
              {model.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
        {/* Training frequency */}
        <div className="space-y-2">
          <Label>Training frequency</Label>
          <div className="flex flex-wrap gap-2 pt-1">
            {trainingFreqOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => toggleArray("trainingFrequency", opt)}
                className={chip((data.trainingFrequency || []).includes(opt))}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Provides leads */}
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/60 p-3">
          <div>
            <Label htmlFor="provides-leads">We provide leads</Label>
            <p className="text-xs text-muted-foreground">Do you provide leads to your agents?</p>
          </div>
          <Switch
            id="provides-leads"
            checked={data.providesLeads}
            onCheckedChange={(checked) => onUpdate({ ...data, providesLeads: checked })}
          />
        </div>
      </div>

      {/* Benefits — includes mentorship, tech tools, and the rest of what you offer */}
      <div className="space-y-3">
        <Label>What do you offer agents?</Label>
        <div role="tablist" aria-label="Benefit groups" className="flex flex-wrap gap-x-3 gap-y-1 border-b border-border">
          {benefitGroups.map((g) => {
            const active = g.title === group.title;
            const count = selectedIn(g);
            return (
              <button
                key={g.title}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveGroup(g.title)}
                className={cn(
                  "-mb-px inline-flex items-center gap-1.5 border-b-2 pb-2 pt-1 text-xs font-semibold transition-colors",
                  active
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {/* "Health & Financial Benefits" is the one title that does not fit the row. */}
                {g.title.replace(/ Benefits$/, "")}
                {count > 0 && (
                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] leading-none text-primary tabular-nums">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* Every group's panel shares one grid cell and only the active one is
            visible, so the step is as tall as its tallest group and the dialog
            does not jump as the admin moves between tabs. */}
        <div className="grid">
          {benefitGroups.map((g) => {
            const active = g.title === group.title;
            return (
              <div
                key={g.title}
                role="tabpanel"
                aria-label={g.title}
                aria-hidden={!active}
                className={cn("col-start-1 row-start-1 flex flex-wrap content-start gap-2", !active && "invisible")}
              >
                {g.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => toggleArray("selectedBenefits", item.label)}
                      className={chip((data.selectedBenefits || []).includes(item.label))}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrgOnboardingStep4;
